import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { normalizeRoadSnapBands } from '../features/geometry-editing/roadSnapBands';
import { setAnalyticsEnabled } from '../lib/posthog';

export interface Settings {
  // Appearance
  theme: 'light' | 'dark' | 'system';
  mapLabels: boolean;
  show3DBuildings: boolean;
  showTerrain: boolean;
  terrainExaggeration: number;

  // Navigation
  avoidTolls: boolean;
  avoidHighways: boolean;
  preferredMode: 'driving' | 'walking' | 'cycling' | 'transit';
  voiceGuidance: boolean;
  hapticFeedback: boolean;

  // Notifications
  trafficAlerts: boolean;
  placeUpdates: boolean;
  promotions: boolean;

  // Privacy
  locationHistory: boolean;
  searchHistory: boolean;
  shareAnalytics: boolean;

  // Language
  language: 'en' | 'tw' | 'ha' | 'ee';
  distanceUnit: 'km' | 'mi';

  /** Field mapping — road endpoint auto-snap on finish (metres). */
  roadSnapAutoRadiusM: number;
  /** Field mapping — search for snap candidates (metres, ≥ auto). */
  roadSnapSearchRadiusM: number;
}

interface SettingsStore {
  settings: Settings;
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  resetSettings: () => void;
  clearSearchHistory: () => void;
  clearLocationHistory: () => void;
}

const defaultSettings: Settings = {
  // Appearance
  theme: 'light',
  mapLabels: true,
  show3DBuildings: false,
  showTerrain: false,
  terrainExaggeration: 1.5,

  // Navigation
  avoidTolls: false,
  avoidHighways: false,
  preferredMode: 'driving',
  voiceGuidance: true,
  hapticFeedback: true,

  // Notifications
  trafficAlerts: true,
  placeUpdates: true,
  promotions: false,

  // Privacy
  locationHistory: true,
  searchHistory: true,
  shareAnalytics: false,

  // Language
  language: 'en',
  distanceUnit: 'km',

  roadSnapAutoRadiusM: 3,
  roadSnapSearchRadiusM: 8,
};

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      settings: defaultSettings,

      updateSetting: (key, value) => {
        set((state) => {
          const next = { ...state.settings, [key]: value };
          const bands = normalizeRoadSnapBands({
            autoM: next.roadSnapAutoRadiusM,
            searchM: next.roadSnapSearchRadiusM,
          });
          next.roadSnapAutoRadiusM = bands.autoM;
          next.roadSnapSearchRadiusM = bands.searchM;
          return { settings: next };
        });
        if (key === 'shareAnalytics') {
          setAnalyticsEnabled(value as boolean);
        }
        if (key === 'searchHistory' && value === false) {
          import('../services/api').then(({ clearRecentSearches }) => {
            clearRecentSearches().catch(() => {});
          });
          import('./mapStore').then(({ useMapStore }) => {
            useMapStore.setState({ recentSearches: [] });
          });
        }
        if (key === 'locationHistory' && value === false) {
          localStorage.removeItem('location-history');
        }
      },

      resetSettings: () => {
        set({ settings: defaultSettings });
      },

      clearSearchHistory: () => {
        localStorage.removeItem('search-history');
        // Also clear in-memory recent searches from mapStore
        import('./mapStore').then(({ useMapStore }) => {
          useMapStore.setState({ recentSearches: [] });
        });
      },

      clearLocationHistory: () => {
        localStorage.removeItem('location-history');
      },
    }),
    {
      name: 'settings-storage',
      merge: (persisted, current) => ({
        ...current,
        settings: {
          ...current.settings,
          ...(persisted as Partial<SettingsStore>)?.settings,
        },
      }),
    }
  )
);
